import { createNewEmbed, createNewEmbedAsync, getAsyncEmbedBuild, IAsyncEmbedBuild } from '@opengov/api-deprecated';
import { IEmbedGet } from '@opengov/embed-types';

export const EXPORT_EMBED_FAILED = 'Export embed build failed';
export const EXPORT_EMBED_TIMED_OUT = 'Export embed build did not finish in time';
export const EXPORT_EMBED_CANCELLED = 'Export embed build polling cancelled';

export const exportEmbedPollTiming = {
  initialDelayMs: 1000,
  maxDelayMs: 5000,
  backoffMultiplier: 2,
  // Matches embed-service's default stale window (EMBED_BUILD_JOB_STALE_MIN = 15), after which it reports a pending
  // build as failed. If that env var is raised, raise this too, or the export gives up while the build still runs.
  maxWaitMs: 15 * 60 * 1000
};

// An embed-service without the async routes answers these; the export then falls back to the synchronous route.
const ASYNC_ROUTE_MISSING_STATUSES = new Set([404, 405]);

type EmbedRequest = Parameters<typeof createNewEmbedAsync>[0];
type AsyncEmbedBuild = IAsyncEmbedBuild['data'];

export type LoadExportEmbedOptions = {
  /** Checked between polls, so a closed export modal stops polling. */
  isCancelled?: () => boolean;
  sleep?: (delayMs: number) => Promise<void>;
  now?: () => number;
};

const sleepFor = (delayMs: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

const httpStatusOf = (error: unknown): number | undefined => (error as { status?: number } | null)?.status;

/**
 * A network failure (fetch rejects with a TypeError) or a 5xx is worth another poll. Anything else stops: a 4xx means
 * the job is gone or not ours, and an unparseable 200 (e.g. an HTML login page) will not fix itself.
 */
const isTransientPollError = (error: unknown): boolean => {
  const status = httpStatusOf(error);
  if (status === undefined) {
    return error instanceof TypeError;
  }
  return status >= 500;
};

function readyEmbedOrThrow(build: AsyncEmbedBuild): IEmbedGet {
  if (build.status !== 'READY' || !build.embed) {
    throw new Error(EXPORT_EMBED_FAILED);
  }
  return build.embed;
}

/** Builds the embed in one request; only used when embed-service has no async routes (e.g. after a rollback). */
async function createEmbedSynchronously(params: EmbedRequest): Promise<AsyncEmbedBuild> {
  // The sync route answers `{ data: <saved embed> }`, the same object the async route returns once READY.
  const response = (await createNewEmbed(params)) as unknown as { data: IEmbedGet };
  return { jobId: '', status: 'READY', embed: response.data };
}

async function startEmbedBuild(params: EmbedRequest): Promise<AsyncEmbedBuild> {
  try {
    return (await createNewEmbedAsync(params)).data;
  } catch (error) {
    const status = httpStatusOf(error);
    if (status !== undefined && ASYNC_ROUTE_MISSING_STATUSES.has(status)) {
      return createEmbedSynchronously(params);
    }
    throw error;
  }
}

/** Polls once; a transient failure keeps the build PENDING so the caller retries within its wait budget. */
async function pollEmbedBuild(build: AsyncEmbedBuild): Promise<AsyncEmbedBuild> {
  try {
    return (await getAsyncEmbedBuild({ jobId: build.jobId })).data;
  } catch (error) {
    if (isTransientPollError(error)) {
      return build;
    }
    throw error;
  }
}

/**
 * Builds the export's summary-table embed through the async route and waits for it. The synchronous
 * `createNewEmbed` runs the whole report query inside one request, which large reports push past the ~60s CDN
 * timeout; here the request returns at once and the build is polled instead.
 */
export async function loadExportEmbed(params: EmbedRequest, options: LoadExportEmbedOptions = {}): Promise<IEmbedGet> {
  const sleep = options.sleep ?? sleepFor;
  const now = options.now ?? Date.now;
  const isCancelled = options.isCancelled ?? (() => false);
  const startedAt = now();
  let delayMs = exportEmbedPollTiming.initialDelayMs;

  let build = await startEmbedBuild(params);
  while (build.status === 'PENDING') {
    if (now() - startedAt > exportEmbedPollTiming.maxWaitMs) {
      throw new Error(EXPORT_EMBED_TIMED_OUT);
    }
    await sleep(delayMs);
    if (isCancelled()) {
      throw new Error(EXPORT_EMBED_CANCELLED);
    }
    build = await pollEmbedBuild(build);
    delayMs = Math.min(delayMs * exportEmbedPollTiming.backoffMultiplier, exportEmbedPollTiming.maxDelayMs);
  }

  return readyEmbedOrThrow(build);
}
